import Pusher, { Channel } from 'pusher-js';
import { getEnv } from '@hub/helpers/getters.store';

export type RealTimeData = { realTime?: boolean };

type ConnectionCallbackArgs = { previous: string; current: string };

enum PusherConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  FAILED = 'failed',
  INITIALIZED = 'initialized',
  UNAVAILABLE = 'unavailable',
}

/**
 *
 */
export default class RealTimeStore {
  private channels: Record<string, Channel> = {};
  private pusher: Pusher | null = null;
  private maxReconnects = 5;
  private reconnectInterval: NodeJS.Timer | null = null;

  /**
   *
   */
  initSocket() {
    if (this.pusher) {
      return;
    }

    const {
      envConfig: { pusherApiKey, pusherCluster },
    } = getEnv();

    const pusher = new Pusher(pusherApiKey, {
      cluster: pusherCluster,
    });

    this.pusher = pusher;

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        pusher.disconnect();
      } else {
        pusher.connect();
      }
    });

    this.pusher.connection.bind(
      'state_change',
      ({ previous, current }: ConnectionCallbackArgs) => {
        console.log(`[Pusher] status changed from ${previous} to ${current}`);
        const isConnected = current === PusherConnectionState.CONNECTED;
        const isDisconnected = current === PusherConnectionState.DISCONNECTED;
        const isPrevNotDisconnected =
          previous !== PusherConnectionState.DISCONNECTED;
        const shouldReconnect =
          isDisconnected && isPrevNotDisconnected && !document.hidden;

        if (shouldReconnect) {
          this.handleReconnection();
        }

        if (isConnected) {
          this.clearReconnectionInterval();
        }
      },
    );
  }

  private subscribeToChannel(channelId: string) {
    if (!this.pusher || this.channels[channelId]) {
      return;
    }

    this.channels[channelId] = this.pusher.subscribe(channelId);
  }

  private clearReconnectionInterval() {
    if (this.reconnectInterval) {
      clearInterval(this.reconnectInterval as unknown as number);
      this.reconnectInterval = null;
    }
  }

  private handleReconnection() {
    let attempts = 0;
    this.reconnectInterval = setInterval(() => {
      const isDisconnected =
        this.pusher?.connection.state === PusherConnectionState.DISCONNECTED;
      const reachedMaxAttempts = attempts >= this.maxReconnects;
      const canReconnect = isDisconnected && !reachedMaxAttempts && this.pusher;

      if (canReconnect) {
        console.log(
          `[Pusher] reconnecting ${attempts + 1}/${this.maxReconnects}`,
        );
        attempts += 1;
        this.pusher!.connect();
      } else {
        this.clearReconnectionInterval();
      }
    }, 5000);
  }

  /**
   *
   */
  registerToEvent<T>(
    channelId: string,
    eventName: string,
    cb: (data: T & RealTimeData) => void,
  ) {
    if (!this.channels[channelId]) {
      this.subscribeToChannel(channelId);
    }
    this.channels[channelId].bind(eventName, (data: T & RealTimeData) => {
      cb({ ...data, realTime: true });
    });
  }

  /**
   *
   */
  disconnect() {
    if (!this.pusher) {
      return;
    }
    Object.values(this.channels).forEach((channel) => channel.unsubscribe());

    this.channels = {};
    this.pusher.disconnect();
    this.pusher = null;
  }
}
